import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  Info,
  Play,
  Target,
  XCircle,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useParams } from 'react-router';

import { ApiError } from '@/api/errors';
import { BotonBiColor, CajaRotulo, Leyenda, TarjetaRotulo } from '@/components/BloquesConsalud';
import { LogoCaso } from '@/components/MarcaCaso';
import { Dona } from '@/components/Graficas';
import { ImagenProtegida } from '@/components/ContenidoProtegido';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { AVATARES } from '@/lib/avatares';
import { animateCSS, celebrate, usePageEnter } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useAtribucionAlCopiar } from '@/lib/atribucion';

import { Cargando, ErrorCarga } from '../curso/componentes';

import { colorCalificacion, etiquetaCategoria, mensajeCalificacion, reloj } from './comun';
import { RUTAS, useResultados, type AttemptCase, type AttemptResults, type Tipo } from './queries';

export function ResultadosPage({ tipo }: { tipo: Tipo }) {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const { data, error, isPending, refetch } = useResultados(tipo, id);
  if (!Number.isInteger(id) || id <= 0) return <Navigate to={RUTAS[tipo].app} replace />;
  if (isPending) return <Cargando alto="h-96" />;
  if (error instanceof ApiError && error.code === 'ATTEMPT_NOT_FINISHED') {
    return <Navigate to={`${RUTAS[tipo].app}/${String(id)}`} replace />;
  }
  if (error) return <ErrorCarga error={error} />;
  return <Resultados tipo={tipo} r={data} onExpirada={() => refetch()} />;
}

function Resultados({
  tipo,
  r,
  onExpirada,
}: {
  tipo: Tipo;
  r: AttemptResults;
  onExpirada: () => unknown;
}) {
  const root = useRef<HTMLDivElement>(null);
  const titulo = useRef<HTMLHeadingElement>(null);
  const [pestana, setPestana] = useState<'resumen' | 'respuestas'>('resumen');
  const [abierto, setAbierto] = useState<number | null>(null);
  usePageEnter(root, [r.id]);
  useEffect(() => {
    if (r.percent >= 70) {
      celebrate(root.current);
      void animateCSS(titulo.current, 'tada', { duration: 900 });
    }
  }, [r.percent]);

  const promedioRojo = r.averageSecondsPerQuestion > 60;
  return (
    <div ref={root} className="relative mx-auto max-w-5xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 ref={titulo} className="titulo-seccion">
            <span className="subrayado-verde">Resultados</span>
          </h1>
          <p className="mt-1 text-sm text-texto-2">
            {tipo === 'simulator' ? `Simulador ${etiquetaCategoria(r.category)}` : 'Examen'} ·{' '}
            {r.mode === 'enarm' ? 'Modo ENARM' : 'Entrenamiento'} · {r.questions} preguntas en{' '}
            {r.cases} casos clínicos
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-modulo"
            data-state={pestana === 'resumen' ? 'active' : undefined}
            onClick={() => setPestana('resumen')}
          >
            Resumen
          </button>
          <button
            type="button"
            className="btn-modulo"
            data-state={pestana === 'respuestas' ? 'active' : undefined}
            onClick={() => setPestana('respuestas')}
          >
            Respuestas
          </button>
        </div>
      </header>

      {pestana === 'resumen' ? (
        <div className="grid gap-x-6 gap-y-10 pt-4 lg:grid-cols-3">
          {/* «Resultados del simulador»: dona, leyenda y aciertos por especialidad */}
          <TarjetaRotulo
            rotulo={tipo === 'simulator' ? 'Resultados del simulador' : 'Resultados del examen'}
            color="marca"
            className="text-center"
          >
            <img
              src="/img/examenes/top-results.png"
              alt=""
              className="mx-auto h-14 w-14 rounded-full bg-white p-1.5 shadow"
            />
            <div className="mt-3 grid place-items-center">
              <Dona percent={r.percent} etiqueta="Correctas" />
            </div>
            <p className={cn('mt-2 text-lg font-extrabold', colorCalificacion(r.percent))}>
              {mensajeCalificacion(r.percent)}
            </p>
            <div className="mt-3 flex justify-center gap-8 text-sm">
              <Leyenda
                color="#1CD0C7"
                etiqueta="Correctas"
                valor={`${String(r.correct)} (${String(r.percent)}%)`}
              />
              <Leyenda
                color="#BADCE5"
                etiqueta="Incorrectas"
                valor={`${String(r.questions - r.correct)} (${String(100 - r.percent)}%)`}
              />
            </div>
            {r.answered < r.questions && (
              <p className="mt-1 text-xs text-texto-3">
                ({r.questions - r.answered} sin contestar)
              </p>
            )}
            <hr className="my-4 border-t border-dashed border-texto-3/40" />
            <h3 className="text-sm font-extrabold uppercase">Aciertos por especialidad</h3>
            <table className="tabla-cs mt-2 text-left">
              <thead>
                <tr>
                  <th>Especialidad</th>
                  <th>%</th>
                  <th>Correctas</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {r.areas.map((a) => (
                  <tr key={a.key}>
                    <td>{a.name}</td>
                    <td
                      className={cn(
                        'font-extrabold tabular',
                        a.total ? colorCalificacion(a.percent) : 'text-texto-3',
                      )}
                    >
                      {a.total ? `${String(a.percent)}%` : '—'}
                    </td>
                    <td className="tabular">{a.correct}</td>
                    <td className="tabular">{a.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TarjetaRotulo>

          <div className="grid content-start gap-x-6 gap-y-10 sm:grid-cols-2 lg:col-span-2">
            {/* «Tu simulador fue de:» */}
            <div className="text-center sm:col-span-2">
              <p className="mx-auto max-w-md rounded-lg bg-marca py-1 font-bold text-white">
                Tu {tipo === 'simulator' ? 'simulador' : 'examen'} fue de:
              </p>
              <p className="mt-1 text-texto-2">
                <strong className="text-texto">{r.questions} preguntas</strong> en {r.cases} casos
                clínicos · {tipo === 'simulator' ? `${etiquetaCategoria(r.category)} · ` : ''}
                {r.mode === 'enarm' ? 'Modo ENARM' : 'Entrenamiento'}
              </p>
            </div>

            <div className="space-y-2">
              <TarjetaRotulo rotulo="Resumen general" color="verde">
                <table className="w-full text-sm">
                  <tbody className="[&_td]:border-b [&_td]:border-[var(--separador)] [&_td]:py-2 [&_tr:last-child_td]:border-0">
                    <tr>
                      <td className="text-texto-2">Tiempo total de la prueba</td>
                      <td
                        className={cn(
                          'text-right font-extrabold tabular',
                          promedioRojo ? 'text-rojo-texto' : 'text-verde-texto',
                        )}
                      >
                        {reloj(r.durationSeconds)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-texto-2">Tiempo promedio por pregunta</td>
                      <td
                        className={cn(
                          'text-right font-extrabold tabular',
                          promedioRojo ? 'text-rojo-texto' : 'text-verde-texto',
                        )}
                      >
                        {reloj(r.averageSecondsPerQuestion)}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-texto-2">Preguntas en español</td>
                      <td className="text-right tabular">{r.questions - r.englishQuestions}</td>
                    </tr>
                    <tr>
                      <td className="text-texto-2">Preguntas en inglés</td>
                      <td className="text-right tabular">{r.englishQuestions}</td>
                    </tr>
                  </tbody>
                </table>
              </TarjetaRotulo>
              <p className="flex gap-1.5 px-1 text-xs text-texto-3">
                <Info size={14} className="mt-0.5 shrink-0 text-azul-texto" />
                <span>
                  Recuerda que <strong>tienes un tiempo máximo de 1 minuto por caso clínico</strong>
                  : asegúrate de que tu tiempo promedio por pregunta sea óptimo para el ENARM.
                </span>
              </p>
            </div>

            <div className="flex flex-col gap-6">
              {r.enarm?.daysLeft != null && r.enarm.daysLeft >= 0 && (
                <div className="panel-vidrio relative text-center">
                  <img
                    src={AVATARES.doctoraSenala}
                    alt=""
                    className="pointer-events-none absolute -top-10 -right-4 h-24 w-auto"
                  />
                  <p>
                    No te confíes, <strong>¡esfuérzate más!</strong>
                  </p>
                  <p className="text-sm text-texto-2">Recuerda que solo tienes…</p>
                  <p className="text-3xl font-extrabold text-azul-texto tabular">
                    {r.enarm.daysLeft} días
                  </p>
                  <p className="text-texto-2">
                    para el ENARM <strong className="text-texto">{r.enarm.year}</strong>
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {tipo === 'simulator' ? (
                  <BotonBiColor icono={<Play size={26} />} variante="verde" to="/simulador">
                    Nuevo simulador
                  </BotonBiColor>
                ) : (
                  <BotonBiColor
                    icono={<ChevronLeft size={28} />}
                    variante="verde"
                    to="/curso-enarm/examenes-prime"
                  >
                    Volver a exámenes
                  </BotonBiColor>
                )}
                <BotonBiColor
                  icono={<CheckCircle2 size={26} />}
                  onClick={() => setPestana('respuestas')}
                >
                  Revisar mis respuestas
                </BotonBiColor>
              </div>
            </div>

            {r.goal && (
              <section
                className={cn(
                  'panel-vidrio border-l-4 sm:col-span-2',
                  r.goal.reached ? 'border-l-verde-500' : 'border-l-amarillo-400',
                )}
              >
                <p className="flex items-start gap-3">
                  <Target className="mt-0.5 shrink-0 text-cian-texto" />
                  <span>
                    El puntaje mínimo de <strong>{r.goal.specialty}</strong> en {r.goal.year} fue{' '}
                    <strong>{r.goal.minimum}</strong>; tu meta es <strong>{r.goal.target}</strong>.{' '}
                    {r.goal.reached
                      ? `Con ${String(r.percent)} puntos, ¡vas por encima de tu meta!`
                      : `Obtuviste ${String(r.percent)}: sigue practicando para alcanzarla.`}
                  </span>
                </p>
              </section>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {r.review.map((c) => (
            <button
              key={c.index}
              type="button"
              className="card-vidrio card-vidrio-hover flex flex-col overflow-hidden border-2 border-azul-600 text-center"
              onClick={() => setAbierto(c.index)}
            >
              <p className="truncate px-2 pt-2 text-xs text-texto-3">{c.specialty}</p>
              <p className="mt-1.5 flex justify-center gap-1 px-2">
                {c.questions.map((q) =>
                  q.selected === null ? (
                    <Circle
                      key={q.id}
                      size={16}
                      className="text-texto-3"
                      aria-label="Sin contestar"
                    />
                  ) : q.isCorrect ? (
                    <CheckCircle2
                      key={q.id}
                      size={16}
                      className="text-verde-texto"
                      aria-label="Correcta"
                    />
                  ) : (
                    <XCircle
                      key={q.id}
                      size={16}
                      className="text-rojo-texto"
                      aria-label="Incorrecta"
                    />
                  ),
                )}
              </p>
              <p className="mt-1 mb-2 text-xs font-bold text-azul-texto tabular">
                {reloj(c.seconds)}
              </p>
              <p className="mt-auto bg-azul-600 py-0.5 text-xs font-extrabold text-white uppercase">
                Caso {c.index + 1}
              </p>
            </button>
          ))}
        </div>
      )}

      <Dialog
        open={abierto !== null}
        onOpenChange={(o) => {
          if (!o) setAbierto(null);
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          {abierto !== null && r.review[abierto] && (
            <Revision
              c={r.review[abierto]}
              total={r.review.length}
              onExpirada={onExpirada}
              onIr={(n) => setAbierto(Math.min(Math.max(n, 0), r.review.length - 1))}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Revision({
  c,
  total,
  onIr,
  onExpirada,
}: {
  c: AttemptCase;
  total: number;
  onIr: (n: number) => void;
  onExpirada: () => unknown;
}) {
  useAtribucionAlCopiar();
  return (
    <div className="space-y-4">
      <DialogTitle>
        Caso {c.index + 1} de {total}
      </DialogTitle>
      <p className="text-sm text-texto-3">
        {c.specialty}
        {c.subspecialty && c.subspecialty !== c.specialty ? ` · ${c.subspecialty}` : ''}
      </p>
      {c.vignette && (
        <p className="superficie-lectura p-4 leading-relaxed whitespace-pre-line">
          <LogoCaso className="mr-2 align-[-2px]" />
          {c.vignette}
        </p>
      )}
      {c.imageUrl && (
        <ImagenProtegida
          src={c.imageUrl}
          clave={`rev-${String(c.index)}`}
          onExpirada={onExpirada}
          alt="Imagen del caso clínico"
          className="mx-auto max-h-80 w-auto rounded-xl"
        />
      )}
      {c.questions.map((q) => {
        const tuya = q.options.find((o) => o.key === q.selected);
        const buena = q.options.find((o) => o.key === q.correctOption);
        return (
          <section key={q.id} className="space-y-2">
            <p className="barra-pregunta normal-case">
              <LogoCaso variante="blanco" className="shrink-0" />
              {q.text}
            </p>
            <CajaRotulo rotulo="Respuesta">
              <p
                className={cn(
                  'flex items-start gap-2 font-bold',
                  q.isCorrect ? 'text-verde-texto' : 'text-rojo-texto',
                )}
              >
                {q.isCorrect ? (
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
                ) : (
                  <XCircle size={18} className="mt-0.5 shrink-0" />
                )}
                Tu respuesta: {tuya ? `${tuya.key}) ${tuya.text}` : 'sin contestar'}
              </p>
              {!q.isCorrect && buena && (
                <p className="mt-1 font-bold">
                  Respuesta correcta: {buena.key}) {buena.text}
                </p>
              )}
            </CajaRotulo>
            <CajaRotulo rotulo="Justificación">
              <p className="text-sm leading-relaxed whitespace-pre-line">{q.justification}</p>
            </CajaRotulo>
          </section>
        );
      })}
      <div className="flex justify-between">
        <button
          type="button"
          className="btn-cs btn-vidrio"
          disabled={c.index === 0}
          onClick={() => onIr(c.index - 1)}
        >
          <ChevronLeft size={16} /> Anterior
        </button>
        <button
          type="button"
          className="btn-cs btn-primario"
          disabled={c.index === total - 1}
          onClick={() => onIr(c.index + 1)}
        >
          Siguiente <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
